import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * CameraCapture — prise de photo continue
 * - Mode live (getUserMedia) : cadrage correct, prise continue, flash visuel
 * - Mode natif (input file) : auto-réouverture après chaque capture
 * - Partagé par tous les modules (prep, réception, etc.)
 * - Rendu via createPortal pour échapper aux transform parents
 */
/**
 * `document` : photo de document (BL, bon de transport). On passe par
 * l'appareil photo du téléphone — pas par le flux vidéo, dont l'image 1080p
 * rend tout petit texte illisible.
 * `single` : une seule photo, pas de réouverture automatique.
 */
export default function CameraCapture({ open, onClose, onCapture, document: modeDocument = false, single = false }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [mode, setMode] = useState(null); // null | 'live' | 'native'
  const [ready, setReady] = useState(false);
  const [count, setCount] = useState(0);
  const [flash, setFlash] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');
  const [uploading, setUploading] = useState(false);

  const closingRef = useRef(false);

  const pushedRef = useRef(false); // Track if we pushed a camera history state

  useEffect(() => {
    if (open) {
      setCount(0);
      setMode(null);
      setUploading(false);
      setUploadStatus(null);
      closingRef.current = false;
      window.__cameraOpen = true;
      pushedRef.current = true;
      if (modeDocument) {
        // Appareil photo natif : pleine définition du capteur
        setMode('native');
        setTimeout(() => fileRef.current?.click(), 250);
      } else {
        tryLiveCamera();
      }
      // Bloquer le scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = '-' + window.scrollY + 'px';
      // Push state pour intercepter le back hardware
      window.history.pushState({ camera: true }, '');

      const onPopState = (e) => {
        // Le back hardware a popé notre state — pas besoin de re-pop
        pushedRef.current = false;
        window.__cameraOpen = false;
        closingRef.current = true;
        stopCamera();
        restoreScroll();
        onClose();
      };
      window.addEventListener('popstate', onPopState);
      return () => {
        window.removeEventListener('popstate', onPopState);
        window.__cameraOpen = false;
        stopCamera();
        restoreScroll();
        // Si on a pushé mais pas encore popé (fermeture via React, pas via back/✕)
        if (pushedRef.current) {
          pushedRef.current = false;
          // Pop silencieusement — skip le handler de Home.jsx
          window.__skipNextPopState = true;
          try { window.history.back(); } catch {}
        }
      };
    }
    return () => { window.__cameraOpen = false; stopCamera(); };
  }, [open]);

  function restoreScroll() {
    const scrollY = document.body.style.top;
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
  }

  useEffect(() => {
    if (open && mode === 'live') {
      stopCamera();
      tryLiveCamera();
    }
  }, [facingMode]);

  // Gérer le switch d'app (TSL ↔ Elixir Prep) — reprendre la caméra au retour
  useEffect(() => {
    if (!open || mode !== 'live') return;
    function handleVisibility() {
      if (document.hidden) {
        // App en arrière-plan → arrêter la caméra proprement
        stopCamera();
      } else {
        // Retour dans l'app → relancer la caméra
        tryLiveCamera();
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [open, mode, facingMode]);

  async function tryLiveCamera() {
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error('Not supported');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          focusMode: { ideal: 'continuous' },
        },
        audio: false,
      });
      // Forcer autofocus continu si supporté
      const track = stream.getVideoTracks()[0];
      if (track) {
        try {
          const caps = track.getCapabilities?.();
          if (caps?.focusMode?.includes('continuous')) {
            await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
          }
        } catch {}
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setReady(true);
        };
      }
      setMode('live');
    } catch (e) {
      console.warn('Live camera failed, using native:', e.message);
      setMode('native');
      setTimeout(() => fileRef.current?.click(), 300);
    }
  }

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setReady(false);
  }

  // ═══ LIVE : prise continue, la caméra reste ouverte ═══
  const [uploadStatus, setUploadStatus] = useState(null); // null | 'uploading' | 'ok' | 'error'

  const takePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    if (uploadStatus === 'uploading') return; // Empêcher double-tap pendant upload
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);

    // Flash immédiat + vibration
    setFlash(true);
    setTimeout(() => setFlash(false), 150);
    try { navigator.vibrate?.([50]); } catch {}

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!blob) return;
    const file = new File([blob], 'photo-' + Date.now() + '.jpg', { type: 'image/jpeg' });

    // Upload avec feedback
    setUploadStatus('uploading');
    try {
      await onCapture(file);
      setCount(c => c + 1);
      setUploadStatus('ok');
    } catch (e) {
      console.error('Photo upload failed:', e);
      setUploadStatus('error');
      try { navigator.vibrate?.([100, 50, 100]); } catch {}
    }
    setTimeout(() => setUploadStatus(null), 1200);
  }, [onCapture, uploadStatus]);

  function switchCamera() {
    stopCamera();
    setFacingMode(f => f === 'environment' ? 'user' : 'environment');
  }

  // ═══ NATIVE : auto-réouverture après chaque capture ═══
  async function handleNativeCapture(e) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      setUploadStatus('uploading');
      try {
        await onCapture(file);
        setCount(c => c + 1);
        setUploadStatus('ok');
      } catch (err) {
        console.error('Native photo upload failed:', err);
        setUploadStatus('error');
      }
    }
    setTimeout(() => setUploadStatus(null), 1200);
    // Reset input
    e.target.value = '';
    setUploading(false);
    if (single) { onClose?.(); return; }
    // Auto-réouverture pour prise continue
    setTimeout(() => {
      if (fileRef.current) fileRef.current.click();
    }, 600);
  }

  function switchToNative() {
    stopCamera();
    setMode('native');
    setTimeout(() => fileRef.current?.click(), 200);
  }

  function handleClose() {
    if (closingRef.current) return;
    closingRef.current = true;
    window.__cameraOpen = false;
    stopCamera();
    restoreScroll();
    // Pop le state camera si on l'a pushé
    if (pushedRef.current) {
      pushedRef.current = false;
      window.__skipNextPopState = true;
      try { window.history.back(); } catch {}
    }
    onClose();
  }

  if (!open) return null;

  // ═══ NATIVE MODE ═══
  if (mode === 'native') {
    return createPortal(
      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        zIndex: 10000, background: 'rgba(0,0,0,0.95)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        touchAction: 'none',
      }}>
        <input ref={fileRef} type="file" accept="image/*" capture="environment"
          onChange={handleNativeCapture} style={{ display: 'none' }} />

        {count > 0 && (
          <div style={{
            padding: '8px 20px', borderRadius: 20,
            background: 'rgba(22,163,74,0.3)', color: '#4ADE80',
            fontSize: 18, fontWeight: 700, marginBottom: 24,
          }}>
            ✓ {count} photo{count > 1 ? 's' : ''}
          </div>
        )}

        {uploading ? (
          <div style={{ color: 'white', fontSize: 14 }}>Enregistrement…</div>
        ) : (
          <>
            <div style={{ color: '#9CA3AF', fontSize: 13, marginBottom: 24, textAlign: 'center', padding: '0 40px' }}>
              {count === 0 ? "L'appareil photo va s'ouvrir" : 'Prends autant de photos que nécessaire'}
            </div>

            <button onClick={() => fileRef.current?.click()} style={{
              width: 80, height: 80, borderRadius: '50%',
              border: '4px solid white', background: 'white',
              cursor: 'pointer', marginBottom: 24,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ fontSize: 28 }}>📷</div>
            </button>
          </>
        )}

        {count > 0 && (
          <button onClick={handleClose} style={{
            padding: '12px 32px', borderRadius: 20,
            border: 'none', background: '#16A34A', color: 'white',
            fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            marginBottom: 12,
          }}>✓ Terminer ({count} photo{count > 1 ? 's' : ''})</button>
        )}

        <button onClick={handleClose} style={{
          padding: '8px 20px', borderRadius: 10, border: 'none',
          background: 'transparent', color: '#6B7280', fontSize: 13,
          cursor: 'pointer', fontFamily: 'inherit',
        }}>{count === 0 ? 'Annuler' : 'Fermer'}</button>
      </div>,
      document.body
    );
  }

  // ═══ LIVE MODE — caméra plein écran, cadrage correct ═══
  return createPortal(
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      zIndex: 10000, background: '#000',
      display: 'flex', flexDirection: 'column',
      touchAction: 'none',
    }}>
      {/* Zone vidéo — contain pour ne pas couper */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
        <video ref={videoRef} playsInline muted autoPlay
          style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* Flash */}
        {flash && (
          <div style={{ position: 'absolute', inset: 0, background: 'white', opacity: 0.5, pointerEvents: 'none', transition: 'opacity 0.15s' }} />
        )}

        {/* Compteur photos */}
        {count > 0 && !uploadStatus && (
          <div style={{
            position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
            padding: '6px 16px', borderRadius: 20,
            background: 'rgba(22,163,74,0.7)', color: 'white',
            fontSize: 14, fontWeight: 700,
          }}>
            ✓ {count} photo{count > 1 ? 's' : ''}
          </div>
        )}

        {/* Upload status */}
        {uploadStatus === 'uploading' && (
          <div style={{
            position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
            padding: '6px 16px', borderRadius: 20,
            background: 'rgba(30,107,60,0.9)', color: 'white',
            fontSize: 14, fontWeight: 700,
          }}>
            ⏳ Sauvegarde…
          </div>
        )}
        {uploadStatus === 'ok' && (
          <div style={{
            position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
            padding: '6px 16px', borderRadius: 20,
            background: 'rgba(22,163,74,0.9)', color: 'white',
            fontSize: 14, fontWeight: 700,
          }}>
            ✅ Photo {count} sauvée
          </div>
        )}
        {uploadStatus === 'error' && (
          <div style={{
            position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
            padding: '8px 20px', borderRadius: 20,
            background: 'rgba(220,38,38,0.9)', color: 'white',
            fontSize: 14, fontWeight: 700,
          }}>
            ❌ Échec — reprendre la photo
          </div>
        )}

        {/* Chargement */}
        {!ready && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.8)', color: 'white', fontSize: 14,
          }}>
            Ouverture caméra…
          </div>
        )}
      </div>

      {/* Barre de contrôles */}
      <div style={{
        padding: '16px 24px', paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
        background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        {/* Fermer */}
        <button onClick={handleClose} style={{
          width: 44, height: 44, borderRadius: '50%', border: 'none',
          background: '#333', color: 'white', fontSize: 18,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>✕</button>

        {/* Déclencheur */}
        <button onClick={takePhoto} disabled={!ready || uploadStatus === 'uploading'} style={{
          width: 70, height: 70, borderRadius: '50%',
          border: '4px solid white', background: (ready && uploadStatus !== 'uploading') ? 'white' : '#555',
          cursor: (ready && uploadStatus !== 'uploading') ? 'pointer' : 'wait',
          transition: 'transform 0.1s',
        }}
        onTouchStart={e => { e.currentTarget.style.transform = 'scale(0.9)'; }}
        onTouchEnd={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: ready ? '#FFF' : '#555', border: '2px solid #111' }} />
        </button>

        {/* Boutons droite */}
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={switchToNative} title="Appareil photo natif" style={{
            width: 44, height: 44, borderRadius: '50%', border: 'none',
            background: '#333', color: 'white', fontSize: 14,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>📱</button>
          <button onClick={switchCamera} style={{
            width: 44, height: 44, borderRadius: '50%', border: 'none',
            background: '#333', color: 'white', fontSize: 16,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>🔄</button>
        </div>
      </div>

      {/* Bouton Terminer flottant */}
      {count > 0 && (
        <div style={{ position: 'absolute', bottom: 110, left: '50%', transform: 'translateX(-50%)' }}>
          <button onClick={handleClose} style={{
            padding: '10px 24px', borderRadius: 20,
            border: 'none', background: '#16A34A', color: 'white',
            fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          }}>✓ Terminer ({count})</button>
        </div>
      )}
    </div>,
    document.body
  );
}
